import { Z_INDEX_ELEVATED_GRID_ITEM } from '@/theme/z-index';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Link } from '@/ui/link/link';
import { SpotlightButton } from '@/ui/spotlight';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';
import { WidgetBox } from '@/ui/widget-box/widget-box';
import { styled } from 'goober';

import { STAGE_PILLS_SLOT_MIN_HEIGHT_PX } from '../stage-pills/spotlight-company-stage-pills';

export const DESCRIPTION_SECTION_MIN_HEIGHT_PX = 62;
export const TAGS_SECTION_MIN_HEIGHT_PX = 34;

export const CardHeader = styled(Flexbox)`
  margin-bottom: 2px;
`;

export const CompanyNameLink = styled(Link)`
  ${({ theme }) => theme.typography.widget.subHeaderBold}
  ${({ theme }) => theme.typography.spotlight.cardName}
  color: ${({ theme }) => theme.colors.spotlight.text.primary};
  margin: 0;
  min-width: 0;
  ${({ theme }) =>
    theme.mixins.transition(
      ['color'],
      theme.duration.moderate,
      theme.easing.out,
    )}

  &:hover {
    color: ${({ theme }) => theme.colors.spotlight.primary};
    text-decoration: none;
  }
`;

export const CompanyNameRow = styled('div')`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
`;

export const WebsiteLink = styled('a')`
  align-items: center;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.spotlight.text.muted};
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  text-decoration: none;
  transform: translateY(2px);
  ${({ theme }) =>
    theme.mixins.transition(
      ['background-color', 'color', 'opacity', 'transform'],
      theme.duration.normal,
      theme.easing.out,
    )}
  width: 24px;

  &:focus-visible {
    opacity: 1;
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
    pointer-events: auto;
    transform: translateY(0);
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.spotlight.surface.default};
    color: ${({ theme }) => theme.colors.spotlight.primary};
  }
`;

export const SectionPlaceholder = styled('span')`
  display: block;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  user-select: none;
`;

export const DescriptionSection = styled('div')`
  min-height: ${DESCRIPTION_SECTION_MIN_HEIGHT_PX}px;
`;

export const CompanyDescription = styled('p')`
  ${({ theme }) => theme.typography.spotlight.cardDescription}
  color: ${({ theme }) => theme.colors.spotlight.text.secondary};
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

export const CompanyDescriptionTooltip = styled(EllipsisTextTooltip)`
  display: block;
`;

export const CardContent = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
`;

export const CardBody = styled(Flexbox)`
  flex: 1;
  min-height: 0;
`;

export const TagsSection = styled('div')<{ $isEmpty: boolean }>`
  max-height: ${TAGS_SECTION_MIN_HEIGHT_PX}px;
  min-height: ${TAGS_SECTION_MIN_HEIGHT_PX}px;
  overflow: visible;

  ${({ $isEmpty }) =>
    $isEmpty
      ? `
    visibility: hidden;
  `
      : ''}
`;

export const TagsRow = styled('div')`
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  min-height: ${TAGS_SECTION_MIN_HEIGHT_PX}px;
  overflow: visible;
`;

export const TagPill = styled('span')`
  ${({ theme }) => theme.typography.spotlight.tagPill}
  background-color: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  max-width: 110px;
  overflow: hidden;
  padding: 7px 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MoreTagsTrigger = styled('button')`
  ${({ theme }) => theme.typography.spotlight.tagPill}
  background-color: ${({ theme }) => theme.colors.spotlight.surface.default};
  border: 0;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.spotlight.text.secondary};
  cursor: pointer;
  flex-shrink: 0;
  padding: 7px 10px;
  ${({ theme }) =>
    theme.mixins.transition(
      ['background-color', 'color'],
      theme.duration.fast,
      theme.easing.out,
    )}

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.colors.spotlight.surface.hover};
    color: ${({ theme }) => theme.colors.spotlight.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.spotlight.border.focus};
    outline-offset: 1px;
  }
`;

export const HiddenTagsPopover = styled('div')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.spotlight.popover};
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
  max-width: 240px;
  padding: 12px;
`;

export const PopoverTagPill = styled('span')`
  ${({ theme }) => theme.typography.spotlight.tagPill}
  background-color: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.spotlight.primary};
  max-width: 200px;
  overflow: hidden;
  padding: 6px 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardFooter = styled('div')`
  border-top: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  flex-shrink: 0;
  padding-top: 14px;
`;

export const CardFooterContent = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
`;

export const StagePillsSlot = styled('div')<{ $hasStagePills: boolean }>`
  flex: 1;
  min-height: ${({ $hasStagePills }) =>
    $hasStagePills ? `${STAGE_PILLS_SLOT_MIN_HEIGHT_PX}px` : '0'};
  min-width: 0;
`;

export const ContactActionSlot = styled('div')`
  flex-shrink: 0;
  padding-top: 10px;
`;

export const ContactTeamButton = styled(SpotlightButton)`
  && {
    ${({ theme }) => theme.typography.spotlight.actionLink}
    color: ${({ theme }) => theme.colors.spotlight.primary};
    flex-shrink: 1;
    max-width: 100%;
    padding: 0;
    text-decoration: none;
    ${({ theme }) =>
      theme.mixins.transition(
        ['color'],
        theme.duration.normal,
        theme.easing.out,
      )}
    white-space: normal;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.spotlight.primaryHover};

    span {
      text-decoration: underline;
    }
  }
`;

export const AnimatedCompanyCard = styled(WidgetBox)<{ $delayMs: number }>`
  ${({ theme }) =>
    theme.animations.enterFadeUp('8px', '0.985', theme.duration.slow)}
  animation-delay: ${({ $delayMs }) => `${$delayMs}ms`};
  border: 1px solid ${({ theme }) => theme.colors.spotlight.border.default};
  border-radius: 16px;
  box-shadow: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 320px;
  padding: 24px;
  ${({ theme }) =>
    theme.mixins.transition(
      ['box-shadow', 'transform'],
      theme.duration.moderate,
      theme.easing.out,
    )}
  will-change: transform;
  z-index: 0;

  &:focus-within {
    z-index: ${Z_INDEX_ELEVATED_GRID_ITEM};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.spotlight.border.hoverCard};
    box-shadow: ${({ theme }) => theme.shadow.spotlight.cardHover};
    transform: translateY(-3px);
    z-index: ${Z_INDEX_ELEVATED_GRID_ITEM};

    a[data-role='company-name-link'] {
      color: ${({ theme }) => theme.colors.spotlight.primary};
    }

    a[data-role='company-website-link'] {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
    will-change: auto;

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.widgets};
      transform: none;
    }
  }
`;
